import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Stethoscope, Shield, Truck, Plane, PlaneTakeoff, Train, Coffee, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const personas = [
  { key: "doctor", label: "Doctor", icon: Stethoscope },
  { key: "nurse", label: "Nurse", icon: Stethoscope },
  { key: "pilot", label: "Pilot", icon: PlaneTakeoff },
  { key: "steward", label: "Cabin Crew", icon: Coffee },
  { key: "train_driver", label: "Train Driver", icon: Train },
  { key: "security", label: "Security", icon: Shield },
  { key: "courier", label: "Courier", icon: Truck },
  { key: "traveler", label: "Traveler", icon: Plane },
  { key: "shift_worker", label: "Other", icon: User },
];

export default function AuthModal({ open, onOpenChange, defaultMode = "login" }) {
  const { login, register, error, setError } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  useEffect(() => { if (open) setMode(defaultMode); }, [defaultMode, open]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [persona, setPersona] = useState("doctor");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    let ok = false;
    if (mode === "login") ok = await login(email, password);
    else ok = await register(name || email.split("@")[0], email, password, persona);
    setBusy(false);
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F141F] border-white/10 text-slate-100 sm:max-w-md rounded-2xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-sm">
            {mode === "login" ? "Sign in to sync your shift schedule." : "Personalized sleep guidance for your shifts."}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={mode} onValueChange={setMode} className="mt-2">
          <TabsList className="bg-slate-900/70 border border-white/10 grid grid-cols-2 w-full">
            <TabsTrigger value="login" data-testid="auth-tab-login">Sign in</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Sign up</TabsTrigger>
          </TabsList>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <TabsContent value="register" className="space-y-4 m-0">
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-ui">Your name</Label>
                <Input
                  data-testid="auth-input-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr. Sarah Chen"
                  className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
                />
              </div>
              <div>
                <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-ui">Persona</Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {personas.map((p) => (
                    <button
                      type="button"
                      key={p.key}
                      onClick={() => setPersona(p.key)}
                      data-testid={`auth-persona-${p.key}`}
                      className={`flex flex-col items-center justify-center gap-1 rounded-xl border p-2 text-[10px] uppercase tracking-widest font-mono-ui transition text-center leading-tight
                        ${persona === p.key ? "border-indigo-400 bg-indigo-500/10 text-indigo-200" : "border-white/10 text-slate-400 hover:bg-white/5"}`}
                    >
                      <p.icon className="w-4 h-4" />
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            </TabsContent>

            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-ui">Email</Label>
              <Input
                data-testid="auth-input-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@hospital.com"
                className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest font-mono-ui">Password</Label>
              <Input
                data-testid="auth-input-password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="mt-1 bg-slate-900/60 border-white/10 text-slate-100"
              />
            </div>

            {error && <div className="text-rose-400 text-sm">{error}</div>}

            <Button
              type="submit"
              data-testid="auth-modal-submit"
              disabled={busy}
              className="w-full bg-indigo-500 hover:bg-indigo-400 text-white h-11 rounded-full glow-indigo"
            >
              {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
